// frontend/src/pages/Company/CompanyForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Building2, Save, Loader2, X, UserPlus, ChevronRight,
  DollarSign, Globe, Tag, AlertCircle
} from "lucide-react";
import "./CompanyForm.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [dropdowns, setDropdowns] = useState({
    businessTypes: [],
    currencies: [],
    countries: []
  });
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Company Data
  const [companyData, setCompanyData] = useState({
    company_name: "",
    business_type: "",
    base_currency: "",
    fiscal_year_start: new Date().getFullYear() + "-01-01",
    country: "",
    status: "active",
  });

  // User Data - Fixed field names to match backend
  const [userData, setUserData] = useState({
    Usercomp_sure_name: "",
    Usercomp_contact: "",
    Usercomp_contact_2: "",   // ✅ NEW
    Usercomp_contact_3: "",   // ✅ NEW
    Usercomp_address: "",
    Usercomp_email: "",
    password: "",
    confirm_password: "",
  });

  const [createUser, setCreateUser] = useState(true);
  const [focused, setFocused] = useState({});

  // Fetch dropdown data
  const fetchDropdowns = async () => {
    try {
      setLoadingDropdowns(true);
      const response = await axios.get(`${url}/api/company/dropdowns/all`, {
        headers: getAuthHeaders(),
      });
      if (response.data.success) {
        setDropdowns({
          businessTypes: response.data.data.business_types || [],
          currencies: response.data.data.currencies || [],
          countries: response.data.data.countries || []
        });
      }
    } catch (error) {
      toast.error("Failed to load dropdown data");
    } finally {
      setLoadingDropdowns(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchDropdowns();

        if (id) {
          setIsEditMode(true);
          const companyRes = await axios.get(`${url}/api/company/${id}`, {
            headers: getAuthHeaders(),
          });
          const company = companyRes.data.data;

          setCompanyData({
            company_name: company.company_name || "",
            business_type: company.business_type?._id || "",
            base_currency: company.base_currency?._id || "",
            fiscal_year_start: company.fiscal_year_start
              ? company.fiscal_year_start.split("T")[0]
              : new Date().getFullYear() + "-01-01",
            country: company.country?._id || "",
            status: company.status || "active",
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanyData({ ...companyData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    // Clear cross-field validation when either password field changes
    if (name === "password" || name === "confirm_password") {
      setErrors((prev) => ({ ...prev, password: "", confirm_password: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field, value) =>
    setFocused({ ...focused, [field]: value && value.length > 0 });

  const validateCompanyData = () => {
    const newErrors = {};
    const required = ["company_name", "business_type", "base_currency", "fiscal_year_start", "country"];

    required.forEach(field => {
      if (!companyData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required company fields");
      return false;
    }

    setErrors({});
    return true;
  };

  const validateUserData = () => {
    const newErrors = {};
    const required = ["Usercomp_sure_name", "Usercomp_contact", "Usercomp_address", "Usercomp_email", "password", "confirm_password"];

    required.forEach(field => {
      if (!userData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (userData.password !== userData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    if (userData.password && userData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (userData.Usercomp_email && !/\S+@\S+\.\S+/.test(userData.Usercomp_email)) {
      newErrors.Usercomp_email = "Email is invalid";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix user form errors");
      return false;
    }

    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (validateCompanyData()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCompanyData()) return;
    if (createUser && !validateUserData()) return;

    try {
      setSubmitting(true);
      // Prepare payload with correct field names
      const payload = {
        ...companyData,
        ...(createUser && {
          user_sure_name: userData.Usercomp_sure_name,
          user_contact: userData.Usercomp_contact,
          user_contact_2: userData.Usercomp_contact_2 || "",   // ✅ NEW
          user_contact_3: userData.Usercomp_contact_3 || "",   // ✅ NEW
          user_address: userData.Usercomp_address,
          user_email: userData.Usercomp_email,
          password: userData.password,
          confirm_password: userData.confirm_password
        })
      };

      let response;
      if (id) {
        response = await axios.put(`${url}/api/company/${id}`, companyData, {
          headers: getAuthHeaders(),
        });
        toast.success("Company updated successfully!");
      } else {
        response = await axios.post(`${url}/api/company`, payload, {
          headers: getAuthHeaders(),
        });
        const msg = createUser
          ? "Company and user created successfully!"
          : "Company created successfully!";
        toast.success(msg);
      }

      setTimeout(() => {
        navigate("/ViewCompany");
      }, 1500);

    } catch (err) {
      // ============================================================
      // Read the specific `field` + `error_code` from the backend
      // and highlight the exact input that conflicted.
      // ============================================================
      const data = err.response?.data || {};
      const errorMsg = data.message || "Failed to save";
      const conflictedField = data.field;

      // Highlight the exact field
      if (conflictedField === "company_name") {
        setErrors((prev) => ({
          ...prev,
          company_name: `This name is already taken${
            data.existing?.company_code ? ` (${data.existing.company_code})` : ""
          }`,
        }));
        // Jump back to step 1 so the user can see the highlighted field
        setStep(1);
      } else if (conflictedField === "user_email") {
        setErrors((prev) => ({
          ...prev,
          Usercomp_email: `This email is already registered${
            data.existing?.Usercomp_code ? ` (${data.existing.Usercomp_code})` : ""
          }`,
        }));
        setStep(2);
      } else if (conflictedField === "password") {
        setErrors((prev) => ({ ...prev, password: data.message }));
        setStep(2);
      } else if (conflictedField === "confirm_password") {
        setErrors((prev) => ({ ...prev, confirm_password: data.message }));
        setStep(2);
      }

      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/ViewCompany');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="company-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${step === 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Company Details</div>
          </div>
          <ChevronRight size={20} className="step-separator" />
          <div className={`step ${step === 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Create Admin User</div>
          </div>
        </div>

        <div className="form-header">
          <Building2 size={24} />
          <h2 className="form-title">
            {isEditMode ? "Edit Company" : step === 1 ? "Create New Company" : "Create Admin User"}
          </h2>
          <p className="form-subtitle">
            {step === 1
              ? "Fill in the company details below. All fields marked with * are required."
              : "Create an admin user for the company. This user will have full access."
            }
          </p>
        </div>

        <form className="multi-step-form" onSubmit={handleSubmit}>
          {/* Step 1: Company Details */}
          {step === 1 && (
            <div className="form-grid">
              {/* Company Name */}
              <div className={`form-field required ${focused.company_name || companyData.company_name ? "filled" : ""} ${errors.company_name ? 'error' : ''}`}>
                <input
                  name="company_name"
                  value={companyData.company_name}
                  onChange={handleCompanyChange}
                  onFocus={() => handleFocus("company_name")}
                  onBlur={() => handleBlur("company_name", companyData.company_name)}
                  placeholder="Enter company name"
                  required
                />
                <label>Company Name</label>
                {errors.company_name && <small className="field-error">{errors.company_name}</small>}
              </div>

              {/* Business Type Dropdown */}
              <div className={`form-field required ${focused.business_type || companyData.business_type ? "filled" : ""} ${errors.business_type ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="business_type"
                    value={companyData.business_type}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("business_type")}
                    onBlur={() => handleBlur("business_type", companyData.business_type)}
                    required
                    disabled={loadingDropdowns}
                  >
                    <option value="">Select Business Type</option>
                    {dropdowns.businessTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <Tag size={16} className="select-icon" />
                </div>
                <label>Business Type</label>
                {errors.business_type && <small className="field-error">{errors.business_type}</small>}
                {loadingDropdowns && <small className="field-hint">Loading...</small>}
                {!loadingDropdowns && dropdowns.businessTypes.length === 0 && (
                  <small className="field-hint error">
                    <AlertCircle size={12} /> No business types available. Please create some first.
                  </small>
                )}
              </div>

              {/* Currency Dropdown */}
              <div className={`form-field required ${focused.base_currency || companyData.base_currency ? "filled" : ""} ${errors.base_currency ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="base_currency"
                    value={companyData.base_currency}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("base_currency")}
                    onBlur={() => handleBlur("base_currency", companyData.base_currency)}
                    required
                    disabled={loadingDropdowns}
                  >
                    <option value="">Select Currency</option>
                    {dropdowns.currencies.map((currency) => (
                      <option key={currency._id} value={currency._id}>
                        {currency.symbol ? `${currency.symbol} - ${currency.name}` : currency.name}
                        {currency.code ? ` (${currency.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <DollarSign size={16} className="select-icon" />
                </div>
                <label>Base Currency</label>
                {errors.base_currency && <small className="field-error">{errors.base_currency}</small>}
                {loadingDropdowns && <small className="field-hint">Loading...</small>}
                {!loadingDropdowns && dropdowns.currencies.length === 0 && (
                  <small className="field-hint error">
                    <AlertCircle size={12} /> No currencies available. Please create some first.
                  </small>
                )}
              </div>

              {/* Fiscal Year Start */}
              <div className={`form-field required ${focused.fiscal_year_start || companyData.fiscal_year_start ? "filled" : ""} ${errors.fiscal_year_start ? 'error' : ''}`}>
                <input
                  type="date"
                  name="fiscal_year_start"
                  value={companyData.fiscal_year_start}
                  onChange={handleCompanyChange}
                  onFocus={() => handleFocus("fiscal_year_start")}
                  onBlur={() => handleBlur("fiscal_year_start", companyData.fiscal_year_start)}
                  required
                />
                <label>Fiscal Year Start</label>
                {errors.fiscal_year_start && <small className="field-error">{errors.fiscal_year_start}</small>}
              </div>

              {/* Country Dropdown */}
              <div className={`form-field required ${focused.country || companyData.country ? "filled" : ""} ${errors.country ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="country"
                    value={companyData.country}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("country")}
                    onBlur={() => handleBlur("country", companyData.country)}
                    required
                    disabled={loadingDropdowns}
                  >
                    <option value="">Select Country</option>
                    {dropdowns.countries.map((country) => (
                      <option key={country._id} value={country._id}>
                        {country.name} {country.code ? `(${country.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <Globe size={16} className="select-icon" />
                </div>
                <label>Country</label>
                {errors.country && <small className="field-error">{errors.country}</small>}
                {loadingDropdowns && <small className="field-hint">Loading...</small>}
                {!loadingDropdowns && dropdowns.countries.length === 0 && (
                  <small className="field-hint error">
                    <AlertCircle size={12} /> No countries available. Please create some first.
                  </small>
                )}
              </div>

              {/* Create Admin User Checkbox */}
              <div className="form-field checkbox-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={createUser}
                    onChange={(e) => setCreateUser(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  Create Admin User for this company
                </label>
                <small className="field-hint">Uncheck if you want to create user later</small>
              </div>

              {/* Status (only in edit mode) */}
              {isEditMode && (
                <div className={`form-field ${focused.status || companyData.status ? "filled" : ""}`}>
                  <select
                    name="status"
                    value={companyData.status}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("status")}
                    onBlur={() => handleBlur("status", companyData.status)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <label>Status</label>
                </div>
              )}
            </div>
          )}

          {/* Step 2: User Details */}
          {step === 2 && createUser && (
            <div className="form-grid">
              <div className={`form-field required ${focused.Usercomp_sure_name || userData.Usercomp_sure_name ? "filled" : ""} ${errors.Usercomp_sure_name ? 'error' : ''}`}>
                <input
                  name="Usercomp_sure_name"
                  value={userData.Usercomp_sure_name}
                  onChange={handleUserChange}
                  onFocus={() => handleFocus("Usercomp_sure_name")}
                  onBlur={() => handleBlur("Usercomp_sure_name", userData.Usercomp_sure_name)}
                  placeholder="Enter user's full name"
                  required
                />
                <label>Full Name</label>
                {errors.Usercomp_sure_name && <small className="field-error">{errors.Usercomp_sure_name}</small>}
              </div>

            {/* Contact Number (Phone 1) */}
<div className={`form-field required ${focused.Usercomp_contact || userData.Usercomp_contact ? "filled" : ""} ${errors.Usercomp_contact ? 'error' : ''}`}>
  <input
    name="Usercomp_contact"
    value={userData.Usercomp_contact}
    onChange={handleUserChange}
    onFocus={() => handleFocus("Usercomp_contact")}
    onBlur={() => handleBlur("Usercomp_contact", userData.Usercomp_contact)}
    placeholder="Enter primary phone number"
    required
  />
  <label>Phone 1 (Primary)</label>
  {errors.Usercomp_contact && <small className="field-error">{errors.Usercomp_contact}</small>}
</div>

{/* ✅ NEW: Phone 2 */}
<div className={`form-field ${focused.Usercomp_contact_2 || userData.Usercomp_contact_2 ? "filled" : ""}`}>
  <input
    name="Usercomp_contact_2"
    value={userData.Usercomp_contact_2}
    onChange={handleUserChange}
    onFocus={() => handleFocus("Usercomp_contact_2")}
    onBlur={() => handleBlur("Usercomp_contact_2", userData.Usercomp_contact_2)}
    placeholder="Enter secondary phone number (optional)"
  />
  <label>Phone 2 (Optional)</label>
</div>

{/* ✅ NEW: Phone 3 */}
<div className={`form-field ${focused.Usercomp_contact_3 || userData.Usercomp_contact_3 ? "filled" : ""}`}>
  <input
    name="Usercomp_contact_3"
    value={userData.Usercomp_contact_3}
    onChange={handleUserChange}
    onFocus={() => handleFocus("Usercomp_contact_3")}
    onBlur={() => handleBlur("Usercomp_contact_3", userData.Usercomp_contact_3)}
    placeholder="Enter tertiary phone number (optional)"
  />
  <label>Phone 3 (Optional)</label>
</div>

              <div className={`form-field required ${focused.Usercomp_address || userData.Usercomp_address ? "filled" : ""} ${errors.Usercomp_address ? 'error' : ''}`}>
                <textarea
                  name="Usercomp_address"
                  value={userData.Usercomp_address}
                  onChange={handleUserChange}
                  onFocus={() => handleFocus("Usercomp_address")}
                  onBlur={() => handleBlur("Usercomp_address", userData.Usercomp_address)}
                  placeholder="Enter full address"
                  rows="3"
                  required
                />
                <label>Address</label>
                {errors.Usercomp_address && <small className="field-error">{errors.Usercomp_address}</small>}
              </div>

              <div className={`form-field required ${focused.Usercomp_email || userData.Usercomp_email ? "filled" : ""} ${errors.Usercomp_email ? 'error' : ''}`}>
                <input
                  type="email"
                  name="Usercomp_email"
                  value={userData.Usercomp_email}
                  onChange={handleUserChange}
                  onFocus={() => handleFocus("Usercomp_email")}
                  onBlur={() => handleBlur("Usercomp_email", userData.Usercomp_email)}
                  placeholder="Enter email address"
                  required
                />
                <label>Email Address</label>
                {errors.Usercomp_email && <small className="field-error">{errors.Usercomp_email}</small>}
              </div>

              <div className={`form-field required ${focused.password || userData.password ? "filled" : ""} ${errors.password ? 'error' : ''}`}>
                <input
                  type="password"
                  name="password"
                  value={userData.password}
                  onChange={handleUserChange}
                  onFocus={() => handleFocus("password")}
                  onBlur={() => handleBlur("password", userData.password)}
                  placeholder="Enter password (min 6 characters)"
                  required
                />
                <label>Password</label>
                {errors.password && <small className="field-error">{errors.password}</small>}
              </div>

              <div className={`form-field required ${focused.confirm_password || userData.confirm_password ? "filled" : ""} ${errors.confirm_password ? 'error' : ''}`}>
                <input
                  type="password"
                  name="confirm_password"
                  value={userData.confirm_password}
                  onChange={handleUserChange}
                  onFocus={() => handleFocus("confirm_password")}
                  onBlur={() => handleBlur("confirm_password", userData.confirm_password)}
                  placeholder="Confirm password"
                  required
                />
                <label>Confirm Password</label>
                {errors.confirm_password && <small className="field-error">{errors.confirm_password}</small>}
              </div>
            </div>
          )}

          <div className="form-actions">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleNext}
                  disabled={submitting || loadingDropdowns}
                >
                  {createUser ? "Next: Create User" : "Create Company Only"}
                  <ChevronRight className="ml-2" size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  <ChevronRight className="mr-2 rotate-180" size={16} />
                  Back
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Company & User
                      <UserPlus className="ml-2" size={16} />
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyForm;
